import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DrillSymbolLegend } from "../components/DrillSymbolLegend";
import { HockeyGoalSymbol } from "../components/HockeyGoalSymbol";
import { RinkDiagram, rinkViewport } from "../components/RinkDiagram";
import { historyReducer } from "../domain/editor-history";
import {
  AGE_GROUPS,
  createDrill,
  DRILL_FOCUS_AREAS,
  ELEMENT_COLORS,
  elementId,
  RINK_TEMPLATES,
  type AgeGroup,
  type DiagramElement,
  type Drill,
  type DrillFocusArea,
  type MarkerElement,
  type MarkerKind,
  type MarkerNumber,
  type PathEnding,
  type PathKind,
  type Point,
} from "../domain/drill";
import { loadDrills, saveDrill } from "../lib/drill-storage";
import { saveCloudDrill } from "../lib/cloud-drill-storage";
import {
  barrierPlacementFromCenter,
  rotationFromCenter,
  snapRotation,
} from "../lib/marker-placement";
import { renderPathGeometry } from "../lib/path-rendering";
import {
  createPdfFile,
  sharePdfFile,
} from "../lib/pdf-share";
import { elementsInSelection, type SelectionBox } from "../lib/selection";

type Tool = "select" | MarkerKind | "line" | "text";

const drillContentSignature = (drill: Drill) =>
  JSON.stringify({ ...drill, updatedAt: "" });
const drillForSave = (drill: Drill) => ({
  ...drill,
  title: drill.title.trim() || "Untitled drill",
});
const markerTools: { id: MarkerKind; label: string; shortcut?: string }[] = [
  { id: "player", label: "Forward", shortcut: "F" },
  { id: "defender", label: "Defender", shortcut: "D" },
  { id: "goalie", label: "Goalie", shortcut: "G" },
  { id: "coach", label: "Coach", shortcut: "C" },
  { id: "puck", label: "Puck" },
  { id: "puckCluster", label: "Puck cluster" },
  { id: "cone", label: "Cone" },
  { id: "goal", label: "Goal" },
  { id: "tire", label: "Tire" },
  { id: "barrier", label: "Barrier" },
  { id: "stick", label: "Stick" },
  { id: "stickTires", label: "Stick + tires" },
  { id: "dummy", label: "Training dummy" },
  { id: "basket", label: "Collection basket" },
  { id: "square", label: "Square" },
  { id: "cross", label: "X marker" },
];
const primaryTools: { id: Tool; label: string; shortcut: string }[] = [
  { id: "select", label: "Select", shortcut: "V" },
  { id: "line", label: "Line", shortcut: "L" },
  { id: "text", label: "Label", shortcut: "T" },
];
const lineStyles: { id: PathKind; label: string }[] = [
  { id: "straight", label: "Straight line" },
  { id: "free", label: "Free / skating" },
  { id: "skatePuck", label: "Skating with puck" },
  { id: "pass", label: "Pass" },
  { id: "backward", label: "Backwards skating" },
  { id: "backwardPuck", label: "Backwards skating with puck" },
  { id: "lateral", label: "Lateral skating" },
];
const lineEndings: { id: PathEnding; label: string }[] = [
  { id: "arrow", label: "Open arrow" },
  { id: "shot", label: "Shot" },
  { id: "stop", label: "Stop / pressure" },
  { id: "direction", label: "Direction arrow" },
  { id: "pivot", label: "Pivot" },
  { id: "none", label: "No ending" },
];
type TireStyle = NonNullable<MarkerElement["tireStyle"]>;
const tireStyles: { id: TireStyle; label: string }[] = [
  { id: "ring", label: "Ring tire" },
  { id: "solid", label: "Go-kart tire" },
];
const playerNumbers = [1, 2, 3, 4, 5] as const;

function ToolIcon({ kind }: { kind: Tool | "legend" }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  return (
    <svg className="tool-icon" viewBox="0 0 32 32" aria-hidden="true">
      {kind === "select" && (
        <path {...common} d="M7 5l15 12-8 1 4 8-4 2-4-8-5 6z" />
      )}
      {kind === "player" && (
        <>
          <circle {...common} cx="16" cy="16" r="11" />
          <text x="16" y="20" textAnchor="middle">
            F
          </text>
        </>
      )}
      {kind === "defender" && (
        <>
          <path {...common} d="M16 5L28 26H4Z" />
          <text x="16" y="22" textAnchor="middle">
            D
          </text>
        </>
      )}
      {kind === "goalie" && (
        <>
          <circle {...common} cx="16" cy="16" r="11" />
          <text x="16" y="20" textAnchor="middle">
            G
          </text>
        </>
      )}
      {kind === "coach" && (
        <>
          <circle {...common} cx="16" cy="16" r="11" />
          <text x="16" y="20" textAnchor="middle">
            C
          </text>
        </>
      )}
      {kind === "puck" && <circle cx="16" cy="16" r="5" fill="currentColor" />}
      {kind === "puckCluster" &&
        [
          [12, 11],
          [20, 11],
          [8, 17],
          [16, 17],
          [24, 17],
          [12, 23],
          [20, 23],
        ].map(([cx, cy]) => (
          <circle
            key={`${cx}-${cy}`}
            cx={cx}
            cy={cy}
            r="2.3"
            fill="currentColor"
          />
        ))}
      {kind === "cone" && (
        <path {...common} stroke="#f47a1f" d="M8 25h16L17 6h-2zM6 26h20" />
      )}
      {kind === "goal" && (
        <g transform="translate(16 16) scale(.48)">
          <HockeyGoalSymbol color="#ef3340" />
        </g>
      )}
      {kind === "tire" && (
        <>
          <circle {...common} cx="16" cy="16" r="10" strokeWidth="4" />
          <circle cx="16" cy="16" r="3" fill="currentColor" />
        </>
      )}
      {kind === "barrier" && (
        <rect {...common} x="4" y="12" width="24" height="8" rx="2" />
      )}
      {kind === "stick" && <path {...common} d="M16 5V27" strokeWidth="2.5" />}
      {kind === "stickTires" && (
        <>
          <path {...common} d="M7 16h18" strokeWidth="2.5" />
          <circle cx="6" cy="16" r="4.5" fill="currentColor" />
          <circle cx="26" cy="16" r="4.5" fill="currentColor" />
        </>
      )}
      {kind === "dummy" && (
        <path {...common} d="M20 8H11v16h9M11 16h6q3 0 4-3" strokeWidth="2.5" />
      )}
      {kind === "basket" && (
        <g {...common}>
          <rect x="2" y="5" width="28" height="22" rx="4" />
          <rect x="6" y="9" width="20" height="14" rx="2" />
        </g>
      )}
      {kind === "square" && (
        <rect {...common} x="7" y="7" width="18" height="18" rx="2" />
      )}
      {kind === "cross" && (
        <path {...common} d="M8 8l16 16M24 8L8 24" strokeWidth="3" />
      )}
      {kind === "line" && (
        <>
          <path {...common} d="M4 22c5-13 9 8 14-5 3-7 6-4 9-7" />
          <path {...common} d="M22 8l5 2-2 5" />
        </>
      )}
      {kind === "text" && (
        <>
          <path {...common} d="M7 8V5h18v3M16 5v22M11 27h10" />
        </>
      )}
      {kind === "legend" && (
        <>
          <circle {...common} cx="16" cy="16" r="11" />
          <path
            {...common}
            d="M12.5 12a3.7 3.7 0 1 1 5.5 3.2c-1.6.9-2 1.8-2 3"
          />
          <circle cx="16" cy="23" r="1.3" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

function RinkTemplateIcon({ template }: { template: Drill["rink"] }) {
  const crop =
    template === "half"
      ? { x: 3, width: 17 }
      : template === "zone"
        ? { x: 3, width: 12 }
        : template === "third"
          ? { x: 12, width: 14 }
          : { x: 3, width: 34 };
  return (
    <svg className="rink-template-icon" viewBox="0 0 40 24" aria-hidden="true">
      <defs>
        <clipPath id={`rink-crop-${template}`}>
          <rect x={crop.x} y="2" width={crop.width} height="20" rx="2" />
        </clipPath>
      </defs>
      <g clipPath={`url(#rink-crop-${template})`}>
        <rect x="3" y="2" width="34" height="20" rx="5" />
        {template !== "blank" && (
          <>
            <path d="M20 2v20M13 2v20M27 2v20" />
            <circle cx="9" cy="8" r="2.5" />
            <circle cx="9" cy="16" r="2.5" />
            <circle cx="31" cy="8" r="2.5" />
            <circle cx="31" cy="16" r="2.5" />
          </>
        )}
      </g>
    </svg>
  );
}

function HistoryIcon({ direction }: { direction: "undo" | "redo" }) {
  return (
    <svg className="history-icon" viewBox="0 0 24 24" aria-hidden="true">
      {direction === "undo" ? (
        <path d="M9 7H5m0 0 3-3M5 7l3 3m-3-3h8a6 6 0 0 1 0 12h-2" />
      ) : (
        <path d="M15 7h4m0 0-3-3m3 3-3 3m3-3h-8a6 6 0 0 0 0 12h2" />
      )}
    </svg>
  );
}

function existingEnding(kind: PathKind): PathEnding {
  if (kind === "shot") return "shot";
  if (kind === "stop" || kind === "pressure") return "stop";
  if (kind === "direction") return "direction";
  if (kind === "pivot") return "pivot";
  return "arrow";
}

function LineIcon({ kind }: { kind: PathKind }) {
  const geometry = renderPathGeometry(kind, [
    { x: 3, y: 12 },
    { x: 53, y: 12 },
  ]);
  return (
    <svg className="picker-icon" viewBox="0 0 58 24" aria-hidden="true">
      <path d={geometry.primary} strokeDasharray={geometry.dash} />
      {geometry.secondary && <path d={geometry.secondary} />}
      {geometry.ticks && <path d={geometry.ticks} />}
    </svg>
  );
}

function EndingIcon({ ending }: { ending: PathEnding }) {
  return (
    <svg className="picker-icon" viewBox="0 0 58 24" aria-hidden="true">
      {ending === "none" ? (
        <path d="M4 12H51" />
      ) : ending === "shot" ? (
        <path d="M4 12H30M30 7H40M30 17H40M40 3L54 12 40 21" />
      ) : ending === "stop" ? (
        <path d="M4 12H45M45 3V21M51 3V21" />
      ) : ending === "pivot" ? (
        <>
          <path d="M4 12H33M31 15a8 8 0 1 1 8 5" />
          <path d="M40 21l-7-2 5-5z" className="filled" />
        </>
      ) : ending === "direction" ? (
        <path d="M4 12H37M54 12L37 3V21Z" className="filled" />
      ) : (
        <path d="M4 12H53M42 3L54 12 42 21" />
      )}
    </svg>
  );
}

function TireStyleIcon({ style }: { style: TireStyle }) {
  return (
    <svg className="picker-icon" viewBox="0 0 58 24" aria-hidden="true">
      {style === "solid" ? (
        <circle cx="29" cy="12" r="7" fill="#111111" />
      ) : (
        <>
          <circle
            cx="29"
            cy="12"
            r="8"
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
          />
          <circle cx="29" cy="12" r="2" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

function VisualPicker<T extends string>({
  label,
  value,
  options,
  onChange,
  icon,
}: {
  label: string;
  value: T;
  options: { id: T; label: string }[];
  onChange: (value: T) => void;
  icon: (value: T) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.id === value) ?? options[0];
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent && event.key !== "Escape") return;
      if (
        event instanceof PointerEvent &&
        pickerRef.current?.contains(event.target as Node)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  return (
    <div className={`visual-picker${open ? " is-open" : ""}`} ref={pickerRef}>
      <span className="visual-picker-label">{label}</span>
      <button
        type="button"
        className="visual-picker-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(!open)}
      >
        {icon(selected.id)}
        <strong>{selected.label}</strong>
        <svg className="picker-chevron" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>
      {open && (
        <div className="visual-picker-menu" role="listbox" aria-label={label}>
          <p>Choose {label.toLowerCase()}</p>
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.id === value}
              key={option.id}
              onClick={() => {
                onChange(option.id);
                setOpen(false);
              }}
            >
              {icon(option.id)}
              <span>{option.label}</span>
              {option.id === value && (
                <svg
                  className="picker-check"
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                >
                  <path d="M3 8.5l3 3L13 4.5" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function EditorRoute() {
  const { drillId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const practiceStation = searchParams.get("practiceStation");
  const initial = useRef<Drill>(
    loadDrills().find((drill) => drill.id === drillId) ?? {
      ...createDrill(),
      folder: searchParams.get("folder")?.trim() || "",
    },
  );
  const lastSavedSignature = useRef(drillContentSignature(initial.current));
  const [history, dispatch] = useReducer(historyReducer, {
    past: [],
    present: initial.current,
    future: [],
  });
  const [tool, setTool] = useState<Tool>("select");
  const holdRotationTimer = useRef<number | undefined>(undefined);
  const copiedElements = useRef<DiagramElement[]>([]);
  const dragMoved = useRef(false);
  const [lineKind, setLineKind] = useState<PathKind>("free");
  const [lineEnding, setLineEnding] = useState<PathEnding>("arrow");
  const [tireStyle, setTireStyle] = useState<TireStyle>("ring");
  const [markerRotations, setMarkerRotations] = useState<
    Partial<Record<MarkerKind, number>>
  >({});
  const [markerNumbers, setMarkerNumbers] = useState<
    Partial<Record<"player" | "defender", MarkerNumber>>
  >({});
  const [selectedId, setSelectedId] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectionBox, setSelectionBox] = useState<SelectionBox>();
  const [drawing, setDrawing] = useState<Point[] | null>(null);
  const [dragging, setDragging] = useState<{
    id: string;
    origin: Point;
    element: DiagramElement;
  }>();
  const [rotating, setRotating] = useState<{
    id: string;
    center: Point;
    kind: MarkerKind;
  }>();
  const [rotationSnap, setRotationSnap] = useState<"horizontal" | "vertical">();
  const [hasCopiedElements, setHasCopiedElements] = useState(false);
  const [placementDraft, setPlacementDraft] = useState<MarkerElement>();
  const [placementOrigin, setPlacementOrigin] = useState<Point>();
  const [hoverPoint, setHoverPoint] = useState<Point>();
  const [activeColor, setActiveColor] = useState("#102032");
  const [showLegend, setShowLegend] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [saved, setSaved] = useState(drillId != null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [shareStatus, setShareStatus] = useState<
    "" | "preparing" | "downloaded" | "error"
  >("");
  const shareFile = useRef<Promise<File> | undefined>(undefined);
  const drill = history.present;
  const selectedElement = drill.elements.find(
    (element) => element.id === selectedId,
  );
  const folderOptions = useMemo(
    () =>
      [...new Set(loadDrills().map((item) => item.folder).filter(Boolean))].sort(),
    [],
  );

  const prepareShareFile = () => {
    if (!shareFile.current) {
      const source = document.querySelector<HTMLElement>(".drill-print-sheet");
      if (!source) return;
      setShareStatus("preparing");
      shareFile.current = createPdfFile(
        source,
        drill.title || "Puckly drill",
        "drill",
      ).catch((error) => {
        shareFile.current = undefined;
        setShareStatus("error");
        throw error;
      });
      void shareFile.current.then(() => setShareStatus(""));
    }
    return shareFile.current;
  };

  const shareDrill = async () => {
    try {
      const file = await prepareShareFile();
      if (!file) return;
      const result = await sharePdfFile(file, drill.title || "Puckly drill");
      if (result === "downloaded") setShareStatus("downloaded");
    } catch (error) {
      if ((error as DOMException).name !== "AbortError") setShareStatus("error");
    }
  };

  useEffect(() => {
    shareFile.current = undefined;
    setShareStatus("");
    const timer = window.setTimeout(() => void prepareShareFile(), 900);
    return () => window.clearTimeout(timer);
    // Generate from the latest rendered drill; a change invalidates the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill]);

  const copySelected = () => {
    copiedElements.current = drill.elements.filter((element) =>
      selectedIds.includes(element.id),
    );
    setHasCopiedElements(copiedElements.current.length > 0);
  };

  const pasteCopied = () => {
    const copies = copiedElements.current.map((element): DiagramElement => {
      const id = elementId();
      if (element.type === "path")
        return {
          ...element,
          id,
          points: element.points.map((point) => ({
            x: point.x + 18,
            y: point.y + 18,
          })),
        };
      return {
        ...element,
        id,
        point: { x: element.point.x + 18, y: element.point.y + 18 },
      };
    });
    if (!copies.length) return;
    dispatch({ type: "add-elements", elements: copies });
    setSelectedIds(copies.map((element) => element.id));
    setSelectedId(copies.at(-1)?.id);
  };

  useEffect(() => {
    const keyboard = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      )
        return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        dispatch({ type: event.shiftKey ? "redo" : "undo" });
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "c") {
        event.preventDefault();
        copySelected();
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "v") {
        event.preventDefault();
        pasteCopied();
        return;
      }
      if (
        (event.key === "Delete" || event.key === "Backspace") &&
        selectedIds.length
      ) {
        dispatch({ type: "delete-elements", ids: selectedIds });
        setSelectedId(undefined);
        setSelectedIds([]);
      }
      if (event.key === "Escape") {
        setShowLegend(false);
        setSelectedId(undefined);
        setSelectedIds([]);
        setSelectionBox(undefined);
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const shortcut = event.key.toLowerCase();
      if (shortcut === "v") setTool("select");
      if (shortcut === "l") setTool("line");
      if (shortcut === "t") setTool("text");
      if (shortcut === "f") setTool("player");
      if (shortcut === "d") setTool("defender");
      if (shortcut === "g") setTool("goalie");
      if (shortcut === "c") setTool("coach");
    };
    window.addEventListener("keydown", keyboard);
    return () => window.removeEventListener("keydown", keyboard);
  }, [selectedIds, drill.elements]);

  useEffect(() => {
    const signature = drillContentSignature(drill);
    if (signature === lastSavedSignature.current) return;

    setSaved(false);
    setAutoSaving(true);
    const timer = window.setTimeout(() => {
      const updated = {
        ...drillForSave(drill),
        updatedAt: new Date().toISOString(),
      };
      saveDrill(updated);
      void saveCloudDrill(updated);
      lastSavedSignature.current = signature;
      setAutoSaving(false);
      setSaved(true);
      if (!drillId) {
        const query =
          practiceStation !== null ? `?practiceStation=${practiceStation}` : "";
        navigate(`/drills/${drill.id}/edit${query}`, { replace: true });
      }
    }, 650);
    return () => window.clearTimeout(timer);
  }, [drill, drillId, navigate, practiceStation]);

  const markerColor = (kind: MarkerKind) =>
    kind === "goal" ? "#ed1c24" : kind === "cone" ? "#f47a1f" : activeColor;

  const createMarker = (kind: MarkerKind, point: Point): MarkerElement => ({
    id: elementId(),
    type: "marker",
    kind,
    point,
    color: markerColor(kind),
    size: kind === "basket" ? 1 : 0.78,
    rotation: markerRotations[kind] ?? 0,
    outlined: kind === "coach",
    number:
      kind === "player" || kind === "defender"
        ? markerNumbers[kind]
        : undefined,
    tireStyle: kind === "tire" ? tireStyle : undefined,
  });

  const pointerDown = (
    point: Point,
    event: React.PointerEvent<SVGSVGElement>,
  ) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectedId(undefined);
    setSelectedIds([]);
    if (tool === "select") {
      setSelectionBox({ start: point, current: point });
      return;
    }
    if (tool === "text") {
      const element: DiagramElement = {
        id: elementId(),
        type: "text",
        point,
        value: "Label",
        color: activeColor,
        fontSize: 20,
      };
      dispatch({ type: "add-element", element });
      setSelectedId(element.id);
      setSelectedIds([element.id]);
      setTool("select");
      return;
    }
    if (tool === "line") {
      setDrawing([point]);
      return;
    }
    setHoverPoint(undefined);
    const markerKind = tool as MarkerKind;
    setPlacementOrigin(point);
    setPlacementDraft(createMarker(markerKind, point));
  };

  const pointerMove = (
    point: Point,
    event: React.PointerEvent<SVGSVGElement>,
  ) => {
    if (
      tool !== "select" &&
      tool !== "line" &&
      tool !== "text" &&
      !drawing &&
      !dragging &&
      !rotating &&
      !placementDraft
    )
      setHoverPoint(point);
    if (drawing) {
      const previous = drawing.at(-1)!;
      if (Math.hypot(point.x - previous.x, point.y - previous.y) > 7)
        setDrawing([...drawing, point]);
    }
    if (selectionBox) setSelectionBox({ ...selectionBox, current: point });
    if (dragging) {
      const dx = point.x - dragging.origin.x;
      const dy = point.y - dragging.origin.y;
      if (Math.hypot(dx, dy) > 3) {
        dragMoved.current = true;
        if (holdRotationTimer.current !== undefined) {
          window.clearTimeout(holdRotationTimer.current);
          holdRotationTimer.current = undefined;
        }
      }
      dispatch({
        type: "update-element",
        id: dragging.id,
        update:
          dragging.element.type === "path"
            ? {
                points: dragging.element.points.map((pathPoint) => ({
                  x: pathPoint.x + dx,
                  y: pathPoint.y + dy,
                })),
              }
            : {
                point: {
                  x: dragging.element.point.x + dx,
                  y: dragging.element.point.y + dy,
                },
              },
      });
    }
    if (rotating) {
      const snapped = snapRotation(rotationFromCenter(rotating.center, point));
      const rotation = snapped.rotation;
      setRotationSnap(snapped.axis);
      dispatch({
        type: "update-element",
        id: rotating.id,
        update: {
          rotation,
        } as Partial<DiagramElement>,
      });
      setMarkerRotations((current) => ({
        ...current,
        [rotating.kind]: rotation,
      }));
    }
    if (placementDraft && placementOrigin) {
      const dx = point.x - placementOrigin.x;
      const dy = point.y - placementOrigin.y;
      if (Math.hypot(dx, dy) > 6)
        if (placementDraft.kind === "barrier") {
          const placement = barrierPlacementFromCenter(placementOrigin, point);
          setRotationSnap(snapRotation(placement.rotation).axis);
          setPlacementDraft({ ...placementDraft, ...placement });
        } else {
          const snapped = snapRotation((Math.atan2(dy, dx) * 180) / Math.PI);
          setRotationSnap(snapped.axis);
          setPlacementDraft({
            ...placementDraft,
            rotation: snapped.rotation,
          });
        }
    }
    if (drawing || dragging || rotating || placementDraft || selectionBox)
      event.preventDefault();
  };

  const pointerUp = () => {
    if (holdRotationTimer.current !== undefined) {
      window.clearTimeout(holdRotationTimer.current);
      holdRotationTimer.current = undefined;
    }
    if (selectionBox) {
      const ids = elementsInSelection(drill.elements, selectionBox);
      setSelectedIds(ids);
      setSelectedId(ids.at(-1));
    }
    if (drawing && drawing.length > 1) {
      dispatch({
        type: "add-element",
        element: {
          id: elementId(),
          type: "path",
          kind: lineKind,
          points: drawing,
          color: activeColor,
          width: 1,
          ending: lineEnding,
        },
      });
    }
    if (placementDraft) {
      dispatch({ type: "add-element", element: placementDraft });
      setMarkerRotations((current) => ({
        ...current,
        [placementDraft.kind]: placementDraft.rotation ?? 0,
      }));
      setSelectedId(undefined);
      setSelectedIds([]);
    }
    setDrawing(null);
    setDragging(undefined);
    setRotating(undefined);
    setRotationSnap(undefined);
    setPlacementDraft(undefined);
    setPlacementOrigin(undefined);
    setSelectionBox(undefined);
  };

  const elementPointerDown = (
    id: string,
    event: React.PointerEvent<Element>,
  ) => {
    if (tool === "line") return;
    event.stopPropagation();
    const additive = event.shiftKey || event.metaKey || event.ctrlKey;
    const alreadySelected = selectedIds.includes(id);
    const nextIds = additive
      ? alreadySelected
        ? selectedIds.filter((item) => item !== id)
        : [...selectedIds, id]
      : alreadySelected && selectedIds.length > 1
        ? selectedIds
        : [id];
    setSelectedIds(nextIds);
    setSelectedId(nextIds.includes(id) ? id : nextIds.at(-1));
    const element = drill.elements.find((item) => item.id === id);
    if (element?.type === "path") {
      setLineKind(
        lineStyles.some((style) => style.id === element.kind)
          ? element.kind
          : "free",
      );
      setLineEnding(element.ending ?? existingEnding(element.kind));
    }
    if (element?.type === "marker" && element.kind === "tire")
      setTireStyle(element.tireStyle ?? "ring");
    if (
      element?.type === "marker" &&
      (element.kind === "player" || element.kind === "defender")
    )
      setMarkerNumbers((current) => ({
        ...current,
        [element.kind]: element.number,
      }));
    if (!element || !nextIds.includes(id)) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    setHoverPoint(undefined);
    if (holdRotationTimer.current !== undefined)
      window.clearTimeout(holdRotationTimer.current);
    holdRotationTimer.current = undefined;
    dragMoved.current = false;
    if (event.altKey && element.type === "marker") {
      setRotating({ id, center: element.point, kind: element.kind });
      return;
    }

    const svg = (event.currentTarget as SVGGraphicsElement).ownerSVGElement;
    const view = rinkViewport(drill.rink, drill.rinkSide);
    const rect = svg?.getBoundingClientRect();
    const origin = rect
      ? {
          x:
            view.x +
            Math.max(
              0,
              Math.min(
                view.width,
                ((event.clientX - rect.left) / rect.width) * view.width,
              ),
            ),
          y:
            view.y +
            Math.max(
              0,
              Math.min(
                view.height,
                ((event.clientY - rect.top) / rect.height) * view.height,
              ),
            ),
        }
      : element.type === "path"
        ? element.points[0]
        : element.point;
    setDragging({ id, origin, element });
    if (alreadySelected && element.type === "marker")
      holdRotationTimer.current = window.setTimeout(() => {
        holdRotationTimer.current = undefined;
        if (dragMoved.current) return;
        setDragging(undefined);
        setRotating({ id, center: element.point, kind: element.kind });
      }, 260);
  };

  const displayedElements = drawing
    ? [
        ...drill.elements,
        {
          id: "drawing",
          type: "path",
          kind: lineKind,
          points: drawing,
          color: activeColor,
          width: 1,
          ending: lineEnding,
        } satisfies DiagramElement,
      ]
    : [
        ...drill.elements,
        ...(placementDraft ? [placementDraft] : []),
        ...(hoverPoint &&
        !placementDraft &&
        !dragging &&
        !rotating &&
        tool !== "select" &&
        tool !== "line" &&
        tool !== "text"
          ? [
              {
                id: "placement-preview",
                type: "marker" as const,
                kind: tool as MarkerKind,
                point: hoverPoint,
                color: markerColor(tool as MarkerKind),
                size: tool === "basket" ? 1 : 0.78,
                rotation: markerRotations[tool as MarkerKind] ?? 0,
                outlined: tool === "coach",
                number:
                  tool === "player" || tool === "defender"
                    ? markerNumbers[tool]
                    : undefined,
                tireStyle: tool === "tire" ? tireStyle : undefined,
              },
            ]
          : []),
      ];

  const save = () => {
    const updated = {
      ...drillForSave(drill),
      updatedAt: new Date().toISOString(),
    };
    saveDrill(updated);
    void saveCloudDrill(updated);
    lastSavedSignature.current = drillContentSignature(updated);
    dispatch({ type: "replace", drill: updated });
    setAutoSaving(false);
    window.setTimeout(() => setSaved(true), 0);
    if (practiceStation !== null) {
      navigate(`/practice?station=${practiceStation}&drillId=${updated.id}`, {
        replace: true,
      });
    } else if (!drillId) {
      navigate(`/drills/${updated.id}/edit`, { replace: true });
    }
  };

  const toggleAge = (age: AgeGroup) => {
    const exists = drill.ageGroups.includes(age);
    const ageGroups = exists
      ? drill.ageGroups.filter((item) => item !== age)
      : [...drill.ageGroups, age];
    if (ageGroups.length) dispatch({ type: "update", update: { ageGroups } });
  };

  const toggleFocus = (focus: DrillFocusArea) => {
    const focusAreas = drill.focusAreas.includes(focus)
      ? drill.focusAreas.filter((item) => item !== focus)
      : [...drill.focusAreas, focus];
    dispatch({ type: "update", update: { focusAreas } });
  };

  const changeColor = (color: string) => {
    setActiveColor(color);
    if (selectedIds.length)
      dispatch({
        type: "update-elements",
        ids: selectedIds,
        update: { color } as Partial<DiagramElement>,
      });
  };

  const updateSelected = (update: Partial<DiagramElement>) => {
    if (selectedId)
      dispatch({ type: "update-element", id: selectedId, update });
  };

  const leaveEditor = () => {
    const signature = drillContentSignature(drill);
    if (signature !== lastSavedSignature.current) {
      const updated = {
        ...drillForSave(drill),
        updatedAt: new Date().toISOString(),
      };
      saveDrill(updated);
      void saveCloudDrill(updated);
      lastSavedSignature.current = signature;
    }
    if (practiceStation !== null) {
      navigate(`/practice?station=${practiceStation}&drillId=${drill.id}`);
      return;
    }
    if (window.history.length > 1) navigate(-1);
    else navigate("/drills");
  };

  const startNewDrill = () => {
    const currentSignature = drillContentSignature(drill);
    if (currentSignature !== lastSavedSignature.current) {
      const updated = {
        ...drillForSave(drill),
        updatedAt: new Date().toISOString(),
      };
      saveDrill(updated);
      void saveCloudDrill(updated);
    }
    const fresh = createDrill();
    lastSavedSignature.current = drillContentSignature(fresh);
    dispatch({ type: "replace", drill: fresh });
    setSelectedId(undefined);
    setSelectedIds([]);
    setTool("select");
    setSaved(false);
    setAutoSaving(false);
    const query =
      practiceStation !== null ? `?practiceStation=${practiceStation}` : "";
    navigate(`/drills/new${query}`);
  };

  return (
    <main className="editor-page">
      <header className="editor-topbar">
        <button
          className="back-button"
          onClick={leaveEditor}
          aria-label={
            practiceStation !== null
              ? "Back to practice planner"
              : "Back to previous page"
          }
        >
          ←
        </button>
        <div className="title-field">
          <label htmlFor="drill-title">Drill name</label>
          <input
            id="drill-title"
            value={drill.title === "Untitled drill" ? "" : drill.title}
            placeholder="Untitled drill"
            onChange={(event) =>
              dispatch({
                type: "update",
                update: { title: event.target.value },
              })
            }
          />
        </div>
        <label className="duration-field">
          Minutes
          <input
            type="number"
            min="1"
            max="90"
            value={drill.durationMinutes}
            onChange={(event) =>
              dispatch({
                type: "update",
                update: {
                  durationMinutes: Math.max(1, Number(event.target.value)),
                },
              })
            }
          />
        </label>
        <div className="history-actions">
          <button
            onClick={() => dispatch({ type: "undo" })}
            disabled={!history.past.length}
            aria-label="Undo"
            title="Undo last step (Ctrl/⌘ + Z)"
          >
            <HistoryIcon direction="undo" />
            <span>Undo</span>
          </button>
          <button
            onClick={() => dispatch({ type: "redo" })}
            disabled={!history.future.length}
            aria-label="Redo"
            title="Redo next step (Ctrl/⌘ + Shift + Z)"
          >
            <HistoryIcon direction="redo" />
            <span>Redo</span>
          </button>
        </div>
        <button
          className="export-drill-button"
          onClick={() => window.print()}
        >
          Export
        </button>
        <button
          className="share-drill-button"
          disabled={shareStatus === "preparing"}
          aria-busy={shareStatus === "preparing"}
          onPointerEnter={() => void prepareShareFile()}
          onPointerDown={() => void prepareShareFile()}
          onClick={() => void shareDrill()}
        >
          Share PDF
        </button>
        <button className="new-drill-button" onClick={startNewDrill}>
          + New drill
        </button>
        <button className="primary-button" onClick={save}>
          {autoSaving ? (
            "Saving…"
          ) : saved ? (
            "Saved"
          ) : (
            <>
              <span className="save-label-full">Save drill</span>
              <span className="save-label-short">Save</span>
            </>
          )}
        </button>
      </header>

      <section className="drill-print-sheet" aria-label="Drill export">
        <header className="drill-print-header">
          <div>
            <p>Puckly Coach · Drill plan</p>
            <h1>{drill.title || "Untitled drill"}</h1>
          </div>
          <img src="/brand/puckly-logo-white.png" alt="Puckly Hockey" />
        </header>
        <div className="drill-print-meta">
          <span>
            <b>Time</b>
            {drill.durationMinutes} min
          </span>
          <span>
            <b>Age groups</b>
            {drill.ageGroups.join(", ")}
          </span>
          <span>
            <b>Focus areas</b>
            {drill.focusAreas.join(", ") || "—"}
          </span>
        </div>
        <div className="drill-print-rink">
          <RinkDiagram
            template={drill.rink}
            rinkSide={drill.rinkSide}
            elements={drill.elements}
          />
        </div>
        <div className="drill-print-notes">
          <section>
            <h2>Description</h2>
            <p>{drill.instructions || "No description added."}</p>
          </section>
          <section>
            <h2>Focus</h2>
            <p>{drill.coachingPoints || "No focus added."}</p>
          </section>
        </div>
      </section>

      <div className="editor-workspace">
        <aside className="toolbox" aria-label="Drawing tools">
          <div className="toolbox-heading">
            <span>Toolbox</span>
            <small>Build your drill</small>
          </div>
          <p className="tool-group-label">Create</p>
          {primaryTools.map((item) => (
            <button
              key={item.id}
              className={`primary-tool ${tool === item.id ? "active" : ""}`}
              onClick={() => setTool(item.id)}
              title={`${item.label} (${item.shortcut})`}
              aria-keyshortcuts={item.shortcut}
            >
              <ToolIcon kind={item.id} />
              <span className="tool-label">{item.label}</span>
              <kbd>{item.shortcut}</kbd>
            </button>
          ))}
          <div className="tool-divider" />
          <p className="tool-group-label">Players</p>
          {markerTools.slice(0, 4).map((item) => (
            <button
              key={item.id}
              className={tool === item.id ? "active" : ""}
              onClick={() => setTool(item.id)}
              title={`${item.label} (${item.shortcut})`}
              aria-keyshortcuts={item.shortcut}
            >
              <ToolIcon kind={item.id} />
              <span className="tool-label">{item.label}</span>
              <kbd>{item.shortcut}</kbd>
            </button>
          ))}
          <div className="tool-divider" />
          <p className="tool-group-label">Equipment</p>
          {markerTools.slice(4).map((item) => (
            <button
              key={item.id}
              className={tool === item.id ? "active" : ""}
              onClick={() => setTool(item.id)}
              title={item.label}
            >
              <ToolIcon kind={item.id} />
              <span className="tool-label">{item.label}</span>
            </button>
          ))}
          <div className="tool-divider" />
          <button
            className="legend-tool"
            onClick={() => setShowLegend(true)}
            title="Open drill symbol legend"
          >
            <ToolIcon kind="legend" />
            <span className="tool-label">Legend</span>
          </button>
        </aside>

        <section className="canvas-column">
          <div className="workspace-controls">
            <div className="control-heading">
              <div>
                <span>Ice layout</span>
                <strong>Choose the area you are coaching</strong>
              </div>
            </div>
            <div className="rink-control-stack">
              <div
                className="rink-picker"
                role="group"
                aria-label="Rink template"
              >
                {RINK_TEMPLATES.map((rink) => (
                  <button
                    key={rink}
                    className={drill.rink === rink ? "active" : ""}
                    onClick={() =>
                      dispatch({ type: "update", update: { rink } })
                    }
                  >
                    <RinkTemplateIcon template={rink} />
                    {rink === "third"
                      ? "1/3 ice"
                      : `${rink[0].toUpperCase()}${rink.slice(1)} ice`}
                  </button>
                ))}
              </div>
              <div className="rink-side-row">
                <span className="side-picker" aria-label="Rink side">
                  <span>Side</span>
                  <button
                    className={drill.rinkSide === "left" ? "active" : ""}
                    onClick={() =>
                      dispatch({ type: "update", update: { rinkSide: "left" } })
                    }
                  >
                    Left side
                  </button>
                  <button
                    className={drill.rinkSide === "right" ? "active" : ""}
                    onClick={() =>
                      dispatch({
                        type: "update",
                        update: { rinkSide: "right" },
                      })
                    }
                  >
                    Right side
                  </button>
                </span>
                <span
                  className={`side-placeholder ${
                    drill.rink === "half" || drill.rink === "zone"
                      ? "hidden"
                      : ""
                  }`}
                  aria-hidden="true"
                >
                  Side selection is available for Half ice and Zone ice
                </span>
              </div>
            </div>
          </div>
          <div className="drawing-options" aria-label="Drawing options">
            {tool === "tire" ||
            (selectedElement?.type === "marker" &&
              selectedElement.kind === "tire") ? (
              <VisualPicker
                label="Tire type"
                value={tireStyle}
                options={tireStyles}
                icon={(style) => <TireStyleIcon style={style} />}
                onChange={(nextTireStyle) => {
                  setTireStyle(nextTireStyle);
                  if (
                    selectedElement?.type === "marker" &&
                    selectedElement.kind === "tire"
                  )
                    updateSelected({
                      tireStyle: nextTireStyle,
                    } as Partial<DiagramElement>);
                }}
              />
            ) : (
              <>
                <VisualPicker
                  label="Line"
                  value={lineKind}
                  options={lineStyles}
                  icon={(kind) => <LineIcon kind={kind} />}
                  onChange={(kind) => {
                    setLineKind(kind);
                    setTool("line");
                    if (selectedElement?.type === "path")
                      updateSelected({ kind } as Partial<DiagramElement>);
                  }}
                />
                <VisualPicker
                  label="Ending"
                  value={lineEnding}
                  options={lineEndings}
                  icon={(ending) => <EndingIcon ending={ending} />}
                  onChange={(ending) => {
                    setLineEnding(ending);
                    setTool("line");
                    if (selectedElement?.type === "path")
                      updateSelected({ ending } as Partial<DiagramElement>);
                  }}
                />
              </>
            )}
            {(tool === "player" || tool === "defender") && (
              <label className="player-number-picker">
                <span>Number</span>
                <select
                  aria-label="Player number"
                  value={markerNumbers[tool] ?? ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    setMarkerNumbers((current) => ({
                      ...current,
                      [tool]: value
                        ? (Number(value) as MarkerNumber)
                        : undefined,
                    }));
                  }}
                >
                  <option value="">No number</option>
                  {playerNumbers.map((number) => (
                    <option key={number} value={number}>
                      {number}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <span className="color-label">Color</span>
            {ELEMENT_COLORS.map((color) => (
              <button
                key={color.value}
                className={activeColor === color.value ? "active" : ""}
                style={{ "--swatch": color.value } as React.CSSProperties}
                onClick={() => changeColor(color.value)}
                aria-label={`${color.name}${selectedElement ? " selected item" : " drawing"} color`}
                title={color.name}
              />
            ))}
          </div>
          <div className={`canvas-stage stage-${drill.rink}`}>
            <div
              className={`canvas-frame canvas-${drill.rink} canvas-side-${drill.rinkSide}`}
            >
              <RinkDiagram
                template={drill.rink}
                rinkSide={drill.rinkSide}
                elements={displayedElements}
                selectedId={tool === "line" ? undefined : selectedId}
                selectedIds={tool === "line" ? [] : selectedIds}
                selectionBox={selectionBox}
                interactive
                onPointerDown={pointerDown}
                onPointerMove={pointerMove}
                onPointerUp={pointerUp}
                onElementPointerDown={elementPointerDown}
                onTextChange={(id, value) =>
                  dispatch({ type: "update-element", id, update: { value } })
                }
              />
              {rotationSnap && (
                <div className={`rotation-snap-hint ${rotationSnap}`}>
                  <span />{" "}
                  {rotationSnap === "horizontal" ? "Horizontal" : "Vertical"}
                </div>
              )}
            </div>
          </div>
          <div className="canvas-footer">
            <span>
              <strong>{drill.elements.length}</strong> objects ·{" "}
              {tool === "select"
                ? "Select an object to move or delete it"
                : tool === "line"
                  ? "Drag on the ice to draw the selected line"
                  : tool === "text"
                    ? "Click the ice to add text"
                    : `Press to place ${tool}; drag before releasing to rotate`}
            </span>
            <button
              className="text-button danger"
              onClick={() => {
                if (
                  drill.elements.length &&
                  window.confirm("Clear the entire diagram?")
                )
                  dispatch({ type: "clear" });
              }}
            >
              Clear diagram
            </button>
          </div>
        </section>

        <aside className="details-panel">
          {selectedElement && (
            <section className="selection-editor">
              <div className="selection-heading">
                <div>
                  <p className="eyebrow">
                    {selectedIds.length > 1
                      ? `${selectedIds.length} selected items`
                      : "Selected item"}
                  </p>
                  <h2>
                    {selectedIds.length > 1
                      ? "Multiple selection"
                      : selectedElement.type === "marker"
                        ? selectedElement.kind.replace(/([A-Z])/g, " $1")
                        : selectedElement.type === "path"
                          ? selectedElement.kind.replace(/([A-Z])/g, " $1")
                          : "Text box"}
                  </h2>
                </div>
                <div className="selection-actions">
                  <button
                    aria-label="Copy selected item"
                    onClick={copySelected}
                  >
                    Copy
                  </button>
                  {hasCopiedElements && (
                    <button
                      aria-label="Paste copied item"
                      onClick={pasteCopied}
                    >
                      Paste
                    </button>
                  )}
                  <button
                    className="danger"
                    aria-label="Delete selected item"
                    onClick={() => {
                      dispatch({
                        type: "delete-elements",
                        ids: selectedIds,
                      });
                      setSelectedId(undefined);
                      setSelectedIds([]);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              {selectedIds.length === 1 &&
                selectedElement.type === "marker" &&
                ![
                  "puck",
                  "puckCluster",
                  "cone",
                  "goal",
                  "tire",
                  "barrier",
                  "stick",
                  "stickTires",
                  "dummy",
                  "square",
                  "cross",
                ].includes(selectedElement.kind) && (
                  <>
                    <label>
                      Label
                      <input
                        maxLength={3}
                        value={selectedElement.label ?? ""}
                        placeholder={
                          selectedElement.kind === "defender"
                            ? "D"
                            : selectedElement.kind === "goalie"
                              ? "G"
                              : selectedElement.kind === "coach"
                                ? "C"
                                : "F"
                        }
                        onChange={(event) =>
                          updateSelected({
                            label: event.target.value,
                          } as Partial<DiagramElement>)
                        }
                      />
                    </label>
                    {(selectedElement.kind === "player" ||
                      selectedElement.kind === "defender") && (
                      <fieldset className="player-number-options">
                        <legend>Player number</legend>
                        <div>
                          <button
                            className={
                              !selectedElement.number ? "selected" : ""
                            }
                            onClick={() => {
                              updateSelected({ number: undefined });
                              setMarkerNumbers((current) => ({
                                ...current,
                                [selectedElement.kind]: undefined,
                              }));
                            }}
                          >
                            None
                          </button>
                          {playerNumbers.map((number) => (
                            <button
                              key={number}
                              className={
                                selectedElement.number === number
                                  ? "selected"
                                  : ""
                              }
                              onClick={() => {
                                updateSelected({ number });
                                setMarkerNumbers((current) => ({
                                  ...current,
                                  [selectedElement.kind]: number,
                                }));
                              }}
                            >
                              {number}
                            </button>
                          ))}
                        </div>
                      </fieldset>
                    )}
                    <label className="check-label">
                      <input
                        type="checkbox"
                        checked={selectedElement.outlined ?? false}
                        onChange={(event) =>
                          updateSelected({
                            outlined: event.target.checked,
                          } as Partial<DiagramElement>)
                        }
                      />
                      Outlined symbol
                    </label>
                  </>
                )}
              {selectedIds.length === 1 &&
                selectedElement.type === "marker" && (
                  <>
                    <label>
                      Size
                      <input
                        type="range"
                        min="0.55"
                        max="1.35"
                        step="0.05"
                        value={selectedElement.size}
                        onChange={(event) =>
                          updateSelected({
                            size: Number(event.target.value),
                          } as Partial<DiagramElement>)
                        }
                      />
                    </label>
                  </>
                )}
              {selectedIds.length === 1 && selectedElement.type === "text" && (
                <label>
                  Text size
                  <input
                    type="range"
                    min="14"
                    max="42"
                    step="1"
                    value={selectedElement.fontSize}
                    onChange={(event) =>
                      updateSelected({
                        fontSize: Number(event.target.value),
                      } as Partial<DiagramElement>)
                    }
                  />
                </label>
              )}
              {selectedIds.length === 1 && selectedElement.type === "path" && (
                <label>
                  Line weight
                  <input
                    type="range"
                    min="0.6"
                    max="2"
                    step="0.1"
                    value={selectedElement.width}
                    onChange={(event) =>
                      updateSelected({
                        width: Number(event.target.value),
                      } as Partial<DiagramElement>)
                    }
                  />
                </label>
              )}
            </section>
          )}
          <p className="eyebrow">Drill details</p>
          <h2>Make it useful on the ice.</h2>
          <label>
            Drill time
            <div className="detail-duration-field">
              <input
                type="number"
                min="1"
                max="90"
                value={drill.durationMinutes}
                onChange={(event) =>
                  dispatch({
                    type: "update",
                    update: {
                      durationMinutes: Math.max(1, Number(event.target.value)),
                    },
                  })
                }
              />
              <span>minutes</span>
            </div>
          </label>
          <label>
            Folder
            {!creatingFolder ? (
              <select
                value={drill.folder}
                onChange={(event) => {
                  if (event.target.value === "__new__") {
                    setCreatingFolder(true);
                    return;
                  }
                  dispatch({ type: "update", update: { folder: event.target.value } });
                }}
              >
                <option value="">No folder</option>
                {folderOptions.map((folder) => (
                  <option value={folder} key={folder}>{folder}</option>
                ))}
                <option value="__new__">＋ Create new folder</option>
              </select>
            ) : (
              <div className="new-folder-field">
                <input
                  autoFocus
                  value={drill.folder}
                  placeholder="New folder name"
                  onChange={(event) =>
                    dispatch({ type: "update", update: { folder: event.target.value } })
                  }
                />
                <button type="button" onClick={() => setCreatingFolder(false)}>Done</button>
              </div>
            )}
          </label>
          <fieldset>
            <legend>Age groups</legend>
            <div className="age-options">
              {AGE_GROUPS.map((age) => (
                <button
                  type="button"
                  key={age}
                  className={drill.ageGroups.includes(age) ? "selected" : ""}
                  onClick={() => toggleAge(age)}
                >
                  {age}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Categories / focus areas</legend>
            <div className="focus-options">
              {DRILL_FOCUS_AREAS.map((focus) => (
                <button
                  type="button"
                  key={focus}
                  className={drill.focusAreas.includes(focus) ? "selected" : ""}
                  onClick={() => toggleFocus(focus)}
                >
                  {focus}
                </button>
              ))}
            </div>
          </fieldset>
          <label>
            Description
            <textarea
              rows={5}
              value={drill.instructions}
              placeholder="Describe the setup and sequence..."
              onChange={(event) =>
                dispatch({
                  type: "update",
                  update: { instructions: event.target.value },
                })
              }
            />
          </label>
          <label>
            Focus
            <textarea
              rows={4}
              value={drill.coachingPoints}
              placeholder="What should the coach emphasize?"
              onChange={(event) =>
                dispatch({
                  type: "update",
                  update: { coachingPoints: event.target.value },
                })
              }
            />
          </label>
          <p className="local-note">
            <span>Local draft</span>This drill is saved only in this browser for
            now. Cloud sync will be added after the editor format is approved.
          </p>
        </aside>
      </div>
      {showLegend && <DrillSymbolLegend onClose={() => setShowLegend(false)} />}
    </main>
  );
}
